// LIVE-DESKTOP (docs/design/2026-11-03-live-desktop.md): the Workspace's blocks as windows on a
// desk. Each window is a block in a frame the person moves (title bar), resizes (edges and
// corner), docks, maximises, pins, closes or brings to the front with a click; the block itself
// is drawn by the canvas screen as before (`renderBlock`), its card header becoming the title bar.
// The agent owns what is on the desk: a `show` opens or restores windows (auto-placed into the
// largest free rectangle), `clear` closes them, `tidy` docks them, `arrange` lays them out (tile,
// focus, cascade, stack), `place` moves them (never a pinned one), `highlight` fronts one and
// dims the rest until Esc. An agent op that would move or dock windows the person touched in the
// last 5 s asks first ("Tidy the board? Tidy / Keep"); opening and spotlighting never ask.
// Geometry is the person's: every change is written back to the session's op log as a `place`
// (a close as a `clear`) and kept in this browser, so a reload restores desk, dock and z-order.
// Keys (no text field focused): 1-9 front, f max, m dock, x close, p pin, t tidy, g tile, Esc,
// / to chat. Under 768 px there is no free placement: the windows stack, front first.
import { Button, cn } from "@companyos/ui";
import {
  CalendarIcon,
  KanbanSquareIcon,
  LayoutGridIcon,
  ListTodoIcon,
  Maximize2Icon,
  Minimize2Icon,
  MinusIcon,
  PinIcon,
  PinOffIcon,
  XIcon,
} from "lucide-react";
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Rnd } from "react-rnd";
import type { CanvasBlock, CanvasState, LiveCanvasOp } from "./canvas-state";
import {
  applyPlace,
  arrange,
  byOpened,
  COLS,
  type Desk,
  close as deskClose,
  dock as deskDock,
  deskLayoutOf,
  deskStoreKey,
  docked,
  fromServer,
  front,
  loadDesk,
  MIN_H,
  MIN_W,
  placeName,
  placeSig,
  ROWS,
  reconcile,
  saveDesk,
  setRect,
  surfaceOrder,
  tidy,
  toggleMax,
  tileTwoColumns,
  togglePin,
  toPlaced,
  type Win,
} from "./desk-layout";
import type { CanvasInteract, CanvasInteraction, DeskWrite } from "./interact";

/** How recent a touch on the desk makes an agent move ask first (live-page.tsx BUSY_MS). */
export const DESK_BUSY_MS = 5_000;
/** Under this width the desk is a stack. */
export const PHONE_MAX_PX = 767;
/** Up to this width (and over a phone's) the desk tiles in two columns for touch. */
export const TABLET_MAX_PX = 1100;
/** Person changes are written back after this quiet time (one `place` per gesture burst). */
const WRITE_DEBOUNCE_MS = 400;

const KIND_LABEL: Record<CanvasBlock["kind"], string> = {
  board: "Board",
  calendar: "Calendar",
  doc: "Doc",
  image_grid: "Images",
  kpi: "KPIs",
  chain: "Chain",
  transcript: "Thread",
  image: "Image",
  video: "Video",
};
export const blockLabel = (b: CanvasBlock) => b.title ?? KIND_LABEL[b.kind];

/**
 * A block drawn inside a window: its card takes the frame's look, its header is the title bar
 * (the drag handle) and carries the window's buttons.
 */
export type WindowChrome = {
  controls: ReactNode;
  handleClass: string;
  phone: boolean;
  tablet?: boolean;
};
export const WindowChromeContext = createContext<WindowChrome | null>(null);
export const useWindowChrome = () => useContext(WindowChromeContext);

export type RenderBlock = (
  block: CanvasBlock,
  o: { large: boolean; itemId: string | null; note: string | null },
) => ReactNode;

function useMedia(query: string): boolean {
  const [on, setOn] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const m = window.matchMedia(query);
    const read = () => setOn(m.matches);
    read();
    m.addEventListener?.("change", read);
    return () => m.removeEventListener?.("change", read);
  }, [query]);
  return on;
}

/** Phone: a stack. Tablet (768 to 1100 px): two-column tiling, touch sizes. Else: free desk. */
export type DeskBand = "phone" | "tablet" | "desktop";
export function useDeskBand(): DeskBand {
  const phone = useMedia(`(max-width: ${PHONE_MAX_PX}px)`);
  const tablet = useMedia(`(min-width: ${PHONE_MAX_PX + 1}px) and (max-width: ${TABLET_MAX_PX}px)`);
  return phone ? "phone" : tablet ? "tablet" : "desktop";
}

/** Tablet: the four corners resize (no thin edges for a finger to miss), each a 44 px target. */
const CORNERS_ONLY = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
};
const CORNER_STYLES = {
  topRight: { width: 44, height: 44, right: -4, top: -4, zIndex: 2 },
  bottomRight: { width: 44, height: 44, right: -4, bottom: -4, zIndex: 2 },
  bottomLeft: { width: 44, height: 44, left: -4, bottom: -4, zIndex: 2 },
  topLeft: { width: 44, height: 44, left: -4, top: -4, zIndex: 2 },
};
/** A visible grip on the two bottom corners (theme tokens, so it reads in light and dark). */
const grip = (side: "right" | "left") => (
  <span
    aria-hidden="true"
    className={cn(
      "pointer-events-none absolute bottom-3 size-4 border-muted-foreground",
      side === "right"
        ? "right-3 rounded-br-md border-r-2 border-b-2"
        : "left-3 rounded-bl-md border-b-2 border-l-2",
    )}
  />
);
const CORNER_GRIPS = { bottomRight: grip("right"), bottomLeft: grip("left") };

/** The surface's size in px (a fallback before it is measured, e.g. in tests). */
function useSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return { w: size.w || 1200, h: size.h || 720 };
}

/** An agent move held back because the person was busy: asked in the bar under the desk. */
export type DeskAsk = { text: string; yes: string; apply: (d: Desk) => Desk };

const names = (blocks: readonly CanvasBlock[], ids: readonly string[] | undefined) => {
  const list = blocks.filter((b) => !ids || ids.includes(b.id)).map(blockLabel);
  return list.length === 0 ? "the windows" : list.length > 2 ? "the windows" : list.join(" and ");
};

/**
 * The desk of one Live session: loaded from this browser and the folded log, kept in step with
 * the canvas's blocks, moved by the agent's ops (each once) and by the person (written back).
 */
export function useDesk({
  companyId,
  sessionId,
  state,
  ops,
  interact,
  write,
}: {
  companyId: string;
  sessionId: string;
  state: CanvasState;
  ops: readonly LiveCanvasOp[];
  interact: CanvasInteract;
  write: DeskWrite;
}) {
  const key = deskStoreKey(companyId, sessionId);
  const [desk, setDesk] = useState<Desk>(() => {
    const now = Date.now();
    return reconcile(
      fromServer(loadDesk(key), state.windows, state.blocks, now),
      state.blocks,
      now,
    );
  });
  const deskRef = useRef(desk);
  deskRef.current = desk;
  const blocksRef = useRef(state.blocks);
  blocksRef.current = state.blocks;
  const lastTouch = useRef(0);
  const [ask, setAsk] = useState<DeskAsk | null>(null);
  const [spot, setSpot] = useState<string | null>(null);
  // Ops already cached when the desk mounts are old news (the fold already holds them).
  const seen = useRef<Set<string> | null>(null);
  if (seen.current === null) seen.current = new Set(ops.map((o) => o.id));
  // Our own `place` writes, to know them when the channel brings them back.
  const sent = useRef(new Set<string>());
  const pending = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    const ids = [...pending.current];
    pending.current.clear();
    const d = deskRef.current;
    const shown = new Set(blocksRef.current.map((b) => b.id));
    const placed = ids.flatMap((id) => {
      const w = d.wins[id];
      return w && shown.has(id) ? [toPlaced(w)] : [];
    });
    const closed = ids.filter((id) => !d.wins[id] && d.closed[id] !== undefined && shown.has(id));
    if (placed.length > 0) {
      for (let i = 0; i < placed.length; i += 16) {
        const windows = placed.slice(i, i + 16);
        sent.current.add(placeSig(windows));
        write({ op: "place", windows });
      }
    }
    if (closed.length > 0) write({ op: "clear", block_ids: closed.slice(0, 16) });
  }, [write]);

  const queue = useCallback(
    (ids: Iterable<string>) => {
      for (const id of ids) pending.current.add(id);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, WRITE_DEBOUNCE_MS);
    },
    [flush],
  );
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        flush();
      }
    },
    [flush],
  );

  /** A person's change: applied, written back, and told to the agent. */
  const person = useCallback(
    (
      f: (d: Desk, now: number) => Desk,
      o: { ids: readonly string[]; action?: CanvasInteraction; detail?: string },
    ) => {
      lastTouch.current = Date.now();
      setDesk((d) => f(d, Date.now()));
      queue(o.ids);
      if (o.action)
        interact({
          action: o.action,
          ...(o.ids[0] ? { block_id: o.ids[0] } : {}),
          ...(o.detail ? { detail: o.detail } : {}),
        });
    },
    [interact, queue],
  );

  useEffect(() => {
    const now = Date.now();
    const fresh = ops.filter((o) => !seen.current?.has(o.id));
    for (const o of fresh) seen.current?.add(o.id);
    const busy = now - lastTouch.current < DESK_BUSY_MS;
    const blocks = state.blocks;
    let ask: DeskAsk | null = null;
    let spotTo: string | null | undefined;
    let moved = false;
    // Computed from the desk as last rendered (not an updater: what it decides is read below).
    const next = ((d0: Desk): Desk => {
      let d = d0;
      const fronts: string[] = [];
      for (const { op } of fresh) {
        if (op.op === "show") {
          // Asked for again: a recent close no longer holds it back.
          const closed = { ...d.closed };
          for (const b of op.blocks) {
            delete closed[b.id];
            fronts.push(b.id);
          }
          d = { ...d, closed };
        }
      }
      d = reconcile(d, blocks, now);
      for (const id of fronts) d = front(d, id);
      for (const { op } of fresh) {
        if (op.op === "arrange") {
          const layout = deskLayoutOf(op.layout);
          const order = op.anchor ? [op.anchor, ...op.order] : op.order;
          const apply = (x: Desk) => arrange(x, layout, order);
          if (busy) ask = { text: `Rearrange the desk (${layout})?`, yes: "Rearrange", apply };
          else {
            d = apply(d);
            moved = true;
          }
        } else if (op.op === "tidy") {
          const ids = (op.block_ids ?? Object.keys(d.wins)).filter(
            (id) => d.wins[id] && d.wins[id]?.state !== "pinned",
          );
          const apply = (x: Desk) => tidy(x, ids);
          if (busy) ask = { text: `Tidy ${names(blocks, op.block_ids)}?`, yes: "Tidy", apply };
          else d = apply(d);
        } else if (op.op === "place") {
          // Our own write coming back changes nothing.
          if (sent.current.delete(placeSig(op.windows))) continue;
          const apply = (x: Desk) => applyPlace(x, op.windows, { agent: true });
          const ids = op.windows.map((w) => w.id);
          if (busy) ask = { text: `Move ${names(blocks, ids)}?`, yes: "Move", apply };
          else d = apply(d);
        } else if (op.op === "highlight" && d.wins[op.block_id]) {
          d = front(d, op.block_id);
          spotTo = op.dim_others === false ? null : op.block_id;
        } else if (op.op === "clear") {
          spotTo = null;
        }
      }
      return d;
    })(deskRef.current);
    if (next !== deskRef.current) {
      deskRef.current = next;
      setDesk(next);
    }
    if (ask) setAsk(ask);
    if (spotTo !== undefined) setSpot(spotTo);
    // A layout the agent made is written back too, so its context reads the desk as it is.
    if (moved) queueMicrotask(() => queue(Object.keys(deskRef.current.wins)));
  }, [ops, state.blocks, queue]);

  useEffect(() => saveDesk(key, desk), [key, desk]);

  const touch = useCallback(() => {
    lastTouch.current = Date.now();
  }, []);
  const accept = useCallback(() => {
    setAsk((a) => {
      if (a) {
        setDesk((d) => a.apply(d));
        queueMicrotask(() => queue(Object.keys(deskRef.current.wins)));
      }
      return null;
    });
  }, [queue]);
  return { desk, setDesk, ask, setAsk, accept, spot, setSpot, touch, person };
}

export type DeskApi = ReturnType<typeof useDesk>;

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]'));
};

/** The desk's keys, when no text field has the focus. */
function useDeskKeys(d: DeskApi, blocks: readonly CanvasBlock[], enabled: boolean) {
  const { desk, person, setSpot } = d;
  const ref = useRef({ desk, blocks });
  ref.current = { desk, blocks };
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const { desk: x, blocks: bs } = ref.current;
      const shown = new Set(bs.map((b) => b.id));
      const wins = surfaceOrder(x).filter((w) => shown.has(w.id));
      const top = wins[wins.length - 1];
      if (e.key === "Escape") {
        setSpot(null);
        return;
      }
      if (/^[1-9]$/.test(e.key)) {
        const w = byOpened(x).filter((v) => shown.has(v.id))[Number(e.key) - 1];
        if (!w) return;
        e.preventDefault();
        person((y, now) => front(y, w.id, now), {
          ids: [w.id],
          action: w.state === "docked" ? "restored" : "fronted",
        });
        return;
      }
      if (e.key === "/") {
        const box = document.querySelector<HTMLElement>(
          '[data-slot="workspace-chat"] [contenteditable="true"], [data-slot="workspace-chat"] textarea, [data-slot="workspace-chat"] input',
        );
        if (box) {
          e.preventDefault();
          box.focus();
        }
        return;
      }
      const all = wins.map((w) => w.id);
      switch (e.key) {
        case "t":
          person((y) => tidy(y), { ids: all, action: "docked", detail: "tidied the desk" });
          break;
        case "g":
          person((y) => arrange(y, "tile"), { ids: all, action: "moved", detail: "tiled" });
          break;
        case "f":
          if (top)
            person((y, now) => toggleMax(y, top.id, now), { ids: [top.id], action: "resized" });
          break;
        case "m":
          if (top) person((y) => deskDock(y, top.id), { ids: [top.id], action: "docked" });
          break;
        case "x":
          if (top)
            person((y, now) => deskClose(y, top.id, now), { ids: [top.id], action: "closed" });
          break;
        case "p":
          if (top)
            person((y, now) => togglePin(y, top.id, now), { ids: [top.id], action: "pinned" });
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, person, setSpot]);
}

/** Stacking ranks as literal classes (Tailwind sees each one); past the last, the top rank. */
const Z_RANKS = [
  "z-[1]",
  "z-[2]",
  "z-[3]",
  "z-[4]",
  "z-[5]",
  "z-[6]",
  "z-[7]",
  "z-[8]",
  "z-[9]",
  "z-[10]",
  "z-[11]",
  "z-[12]",
  "z-[13]",
  "z-[14]",
  "z-[15]",
  "z-[16]",
  "z-[17]",
  "z-[18]",
  "z-[19]",
  "z-[20]",
] as const;
const zRank = (i: number) => Z_RANKS[Math.max(0, Math.min(i, Z_RANKS.length - 1))];

/** Suggestions on an empty desk: tapping one opens it. */
export type Ghost = "board" | "calendar" | "today";

export function DeskSurface({
  desk: d,
  blocks,
  highlight,
  renderBlock,
  onGhost,
}: {
  desk: DeskApi;
  blocks: readonly CanvasBlock[];
  highlight: CanvasState["highlight"];
  renderBlock: RenderBlock;
  onGhost?: (g: Ghost) => void;
}) {
  const { desk, spot, touch, person } = d;
  const band = useDeskBand();
  const narrow = band === "phone";
  const tablet = band === "tablet";
  const ref = useRef<HTMLDivElement>(null);
  const size = useSize(ref);
  const cw = size.w / COLS;
  const rh = size.h / ROWS;
  const byId = new Map(blocks.map((b) => [b.id, b]));
  useDeskKeys(d, blocks, true);

  // Tablet: the windows tile in two columns on entering the band and whenever the set of open
  // windows changes; the person's own drags and resizes stand until then.
  const shownSig = surfaceOrder(desk)
    .filter((w) => byId.has(w.id) && w.state !== "pinned")
    .map((w) => w.id)
    .sort()
    .join(",");
  const tiledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!tablet) {
      tiledFor.current = null;
      return;
    }
    if (tiledFor.current === shownSig) return;
    const first = tiledFor.current === null;
    tiledFor.current = shownSig;
    if (shownSig === "" || (first && shownSig.split(",").length < 2)) return;
    person((x) => tileTwoColumns(x), {
      ids: shownSig.split(","),
      action: "moved",
      detail: "tiled in two columns",
    });
  }, [tablet, shownSig, person]);

  const controls = (w: Win, b: CanvasBlock) => {
    const label = blockLabel(b);
    // Tablet: 44 px targets (no hover-only controls: every button is always drawn).
    const btn = cn(
      "desk-nodrag text-muted-foreground hover:text-foreground",
      tablet ? "size-11" : "size-7",
    );
    return (
      <span className="desk-nodrag ml-1 flex shrink-0 items-center" data-slot="window-controls">
        {narrow ? null : (
          <Button
            variant="ghost"
            size="icon"
            className={btn}
            aria-label={w.state === "pinned" ? `Unpin ${label}` : `Pin ${label}`}
            aria-pressed={w.state === "pinned"}
            title={w.state === "pinned" ? "Unpin (p)" : "Pin (p)"}
            onClick={() =>
              person((x, now) => togglePin(x, w.id, now), {
                ids: [w.id],
                action: "pinned",
                detail: w.state === "pinned" ? "unpinned" : "pinned",
              })
            }
          >
            {w.state === "pinned" ? (
              <PinOffIcon aria-hidden="true" />
            ) : (
              <PinIcon aria-hidden="true" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={btn}
          aria-label={`Dock ${label}`}
          title="Dock (m)"
          onClick={() => person((x) => deskDock(x, w.id), { ids: [w.id], action: "docked" })}
        >
          <MinusIcon aria-hidden="true" />
        </Button>
        {narrow ? null : (
          <Button
            variant="ghost"
            size="icon"
            className={btn}
            aria-label={w.state === "max" ? `Restore ${label}` : `Maximise ${label}`}
            title={w.state === "max" ? "Restore (f)" : "Maximise (f)"}
            onClick={() =>
              person((x, now) => toggleMax(x, w.id, now), {
                ids: [w.id],
                action: "resized",
                detail: w.state === "max" ? "restored size" : "full screen",
              })
            }
          >
            {w.state === "max" ? (
              <Minimize2Icon aria-hidden="true" />
            ) : (
              <Maximize2Icon aria-hidden="true" />
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={btn}
          aria-label={`Close ${label}`}
          title="Close (x)"
          onClick={() =>
            person((x, now) => deskClose(x, w.id, now), { ids: [w.id], action: "closed" })
          }
        >
          <XIcon aria-hidden="true" />
        </Button>
      </span>
    );
  };

  const wins = surfaceOrder(desk).filter((w) => byId.has(w.id));
  const topId = wins[wins.length - 1]?.id;
  const body = (w: Win, b: CanvasBlock) => {
    const lit = highlight?.block_id === b.id ? highlight : null;
    return (
      <WindowChromeContext.Provider
        value={{ controls: controls(w, b), handleClass: "desk-handle", phone: narrow, tablet }}
      >
        <div
          data-block={b.id}
          data-highlighted={lit ? "true" : undefined}
          className="flex h-full min-h-0 min-w-0 flex-col"
        >
          {renderBlock(b, {
            large: narrow || w.state === "max" || w.w * w.h >= 160,
            itemId: lit?.item_id ?? null,
            note: lit?.note ?? null,
          })}
        </div>
      </WindowChromeContext.Provider>
    );
  };

  if (narrow)
    return (
      <div data-slot="desk" data-mode="stack" className="flex min-w-0 flex-col gap-3">
        {wins.length === 0 ? <EmptyDesk onGhost={onGhost} stacked /> : null}
        {[...wins].reverse().map((w) => {
          const b = byId.get(w.id);
          if (!b) return null;
          return (
            <div
              key={w.id}
              data-window={w.id}
              data-state={w.state}
              className={cn(
                "max-h-[70vh] min-w-0 animate-in fade-in [&:not(:has([data-block]>*))]:hidden",
                highlight?.block_id === w.id && "rounded-lg ring-2 ring-primary",
              )}
            >
              {body(w, b)}
            </div>
          );
        })}
      </div>
    );

  return (
    <div
      ref={ref}
      data-slot="desk"
      data-mode={tablet ? "tablet" : "free"}
      className="relative h-[calc(100dvh-16rem)] min-h-[480px] min-w-0 overflow-hidden rounded-xl border border-border bg-muted/40 bg-[radial-gradient(var(--color-border)_1px,transparent_1px)] [background-size:20px_20px]"
      onPointerDownCapture={touch}
    >
      {wins.length === 0 ? <EmptyDesk onGhost={onGhost} /> : null}
      {/* A stable DOM order (opened first): moving a node on mousedown would swallow the click
          that fronted it. The stacking order is a rank class (no inline z-index). */}
      {[...wins]
        .sort((a, b) => a.opened - b.opened || (a.id < b.id ? -1 : 1))
        .map((w) => {
          const b = byId.get(w.id);
          if (!b) return null;
          const max = w.state === "max";
          const lit = highlight?.block_id === w.id;
          const dim = spot !== null && spot !== w.id;
          return (
            <Rnd
              key={w.id}
              data-window={w.id}
              data-state={w.state}
              data-front={w.id === topId ? "true" : undefined}
              data-dimmed={dim ? "true" : undefined}
              size={max ? { width: size.w, height: size.h } : { width: w.w * cw, height: w.h * rh }}
              position={max ? { x: 0, y: 0 } : { x: w.x * cw, y: w.y * rh }}
              bounds="parent"
              dragHandleClassName="desk-handle"
              cancel=".desk-nodrag"
              dragGrid={[cw, rh]}
              resizeGrid={[cw, rh]}
              minWidth={MIN_W * cw}
              minHeight={MIN_H * rh}
              disableDragging={max || w.state === "pinned"}
              enableResizing={
                max || w.state === "pinned"
                  ? false
                  : tablet
                    ? CORNERS_ONLY
                    : true
              }
              {...(tablet
                ? { resizeHandleStyles: CORNER_STYLES, resizeHandleComponent: CORNER_GRIPS }
                : {})}
              className={cn(
                "p-1.5 transition-opacity duration-200 [&:not(:has([data-block]>*))]:hidden!",
                zRank(wins.indexOf(w)),
                // Hover brings a dimmed window back on a mouse; touch taps it (below).
                dim && (tablet ? "opacity-40" : "opacity-40 hover:opacity-100"),
              )}
              onMouseDown={() => {
                if (w.id !== topId)
                  person((x, now) => front(x, w.id, now), { ids: [w.id], action: "fronted" });
              }}
              onDragStop={(_e, p) => {
                const x = Math.round(p.x / cw);
                const y = Math.round(p.y / rh);
                if (x === w.x && y === w.y) return;
                person((v, now) => setRect(v, w.id, { x, y, w: w.w, h: w.h }, now), {
                  ids: [w.id],
                  action: "moved",
                  detail: `to ${placeName({ ...w, x, y })}`,
                });
              }}
              onResizeStop={(_e, _dir, el, _delta, p) => {
                person(
                  (v, now) =>
                    setRect(
                      v,
                      w.id,
                      { x: p.x / cw, y: p.y / rh, w: el.offsetWidth / cw, h: el.offsetHeight / rh },
                      now,
                    ),
                  { ids: [w.id], action: "resized" },
                );
              }}
            >
              <div
                className={cn(
                  "flex h-full w-full min-w-0 flex-col rounded-lg animate-in fade-in zoom-in-95 duration-200",
                  lit && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                )}
                // A touch front the window and ends the spotlight (compat mouse events are not
                // relied on for touch).
                onPointerDown={
                  tablet
                    ? (e) => {
                        if (e.pointerType !== "touch") return;
                        if (dim) d.setSpot(null);
                        if (w.id !== topId)
                          person((x, now) => front(x, w.id, now), {
                            ids: [w.id],
                            action: "fronted",
                          });
                      }
                    : undefined
                }
              >
                {body(w, b)}
              </div>
            </Rnd>
          );
        })}
    </div>
  );
}

const GHOSTS: { g: Ghost; label: string; icon: typeof CalendarIcon; at: string }[] = [
  { g: "board", label: "Board", icon: KanbanSquareIcon, at: "left-[4%] top-[8%] h-[44%] w-[46%]" },
  {
    g: "calendar",
    label: "Calendar",
    icon: CalendarIcon,
    at: "right-[4%] top-[8%] h-[44%] w-[44%]",
  },
  { g: "today", label: "Today", icon: ListTodoIcon, at: "bottom-[8%] left-[28%] h-[34%] w-[44%]" },
];

function EmptyDesk({
  onGhost,
  stacked,
}: {
  onGhost?: ((g: Ghost) => void) | undefined;
  stacked?: boolean;
}) {
  return (
    <div
      data-slot="desk-empty"
      className={cn(stacked ? "flex flex-col gap-2" : "absolute inset-0")}
    >
      {onGhost
        ? GHOSTS.map(({ g, label, icon: Icon, at }) => (
            <button
              key={g}
              type="button"
              data-ghost={g}
              onClick={() => onGhost(g)}
              className={cn(
                "flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card/40 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-foreground",
                stacked ? "h-14 w-full" : `absolute ${at}`,
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
              {label}
            </button>
          ))
        : null}
      <p
        className={cn(
          "text-center text-xs text-muted-foreground",
          stacked ? "py-2" : "pointer-events-none absolute inset-x-0 top-[55%]",
        )}
      >
        Ask for something, or tap one to open it.
      </p>
    </div>
  );
}

/** The strip under the desk: docked windows as chips (a click restores), Tile, Tidy, and talk. */
export function Dock({
  desk: d,
  blocks,
  children,
}: {
  desk: DeskApi;
  blocks: readonly CanvasBlock[];
  children?: ReactNode;
}) {
  const { desk, ask, setAsk, accept, person } = d;
  // Tablet: a slimmer strip with 44 px touch targets, the empty-dock text dropped.
  const tablet = useDeskBand() === "tablet";
  const byId = new Map(blocks.map((b) => [b.id, b]));
  const chips = docked(desk).filter((w) => byId.has(w.id));
  const onSurface = surfaceOrder(desk).filter((w) => byId.has(w.id));
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {ask ? (
        <div
          role="status"
          data-slot="desk-prompt"
          className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-sm"
        >
          <span className="min-w-0 flex-1 font-medium">{ask.text}</span>
          <Button size="sm" onClick={accept}>
            {ask.yes}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAsk(null)}>
            Keep
          </Button>
        </div>
      ) : null}
      <nav
        aria-label="Dock"
        data-slot="dock"
        data-band={tablet ? "tablet" : undefined}
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3",
          tablet ? "py-1" : "py-2",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {chips.length === 0 ? (
            <span className={cn("text-xs text-muted-foreground", tablet && "sr-only")}>
              Docked windows show here.
            </span>
          ) : (
            chips.map((w) => {
              const b = byId.get(w.id);
              if (!b) return null;
              return (
                <Button
                  key={w.id}
                  size="sm"
                  variant="outline"
                  data-dock-chip={w.id}
                  className={cn(
                    "animate-in fade-in slide-in-from-bottom-1",
                    tablet ? "h-11 max-w-32" : "max-w-48",
                  )}
                  title={`Restore ${blockLabel(b)}`}
                  onClick={() =>
                    person((x, now) => front(x, w.id, now), { ids: [w.id], action: "restored" })
                  }
                >
                  <span className="truncate">{blockLabel(b)}</span>
                </Button>
              );
            })
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 max-md:hidden">
          <Button
            size="sm"
            variant="ghost"
            disabled={onSurface.length < 2}
            onClick={() =>
              person((x) => arrange(x, "tile"), {
                ids: onSurface.map((w) => w.id),
                action: "moved",
                detail: "tiled",
              })
            }
            title="Tile the windows (g)"
          >
            <LayoutGridIcon aria-hidden="true" />
            Tile
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={onSurface.length === 0}
            onClick={() =>
              person((x) => tidy(x), {
                ids: onSurface.map((w) => w.id),
                action: "docked",
                detail: "tidied the desk",
              })
            }
            title="Dock every unpinned window (t)"
          >
            Tidy
          </Button>
        </div>
        {children}
      </nav>
    </div>
  );
}
